import React, { useState, useRef, useEffect } from 'react';
import { useTranslations, AVAILABLE_LANGUAGES } from '../utils/translations';
import YDALogo from '../assets/YDA-logo.png';
import './TopBar.css';

const TopBar = ({
  currentLanguage,
  onLanguageChange,
  isAudioMuted,
  onAudioToggle,
  currentSlideIndex,
  totalSlides,
  onVisibilityChange,
  onSlideNavigate,
  completedSlides = [],
  lessonData
}) => {
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isSlideDropdownOpen, setIsSlideDropdownOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const dropdownRef = useRef(null);
  const slideDropdownRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const { t } = useTranslations(currentLanguage);

  // Notify parent when visibility changes
  useEffect(() => {
    if (onVisibilityChange) {
      onVisibilityChange(isVisible);
    }
    // Close dropdowns when top bar hides
    if (!isVisible) {
      setIsLanguageDropdownOpen(false);
      setIsSlideDropdownOpen(false);
    }
  }, [isVisible, onVisibilityChange]);

  // Auto-hide functionality
  useEffect(() => {
    const handleInteraction = () => {
      setIsVisible(true);
      
      // Clear existing timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      
      // Set new timeout to hide after 3 seconds of inactivity
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 3000);
    };

    // Add event listeners for user interactions
    const events = ['click', 'touchstart', 'mousemove', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, handleInteraction);
    });

    // Initial timeout
    handleInteraction();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleInteraction);
      });
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLanguageDropdownOpen(false);
      }
      if (slideDropdownRef.current && !slideDropdownRef.current.contains(event.target)) {
        setIsSlideDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);



  const toggleLanguageDropdown = () => {
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
    setIsSlideDropdownOpen(false); // Close slide dropdown when opening language
  };

  const toggleSlideDropdown = () => {
    setIsSlideDropdownOpen(!isSlideDropdownOpen);
    setIsLanguageDropdownOpen(false); // Close language dropdown when opening slide
  };

  const handleLanguageSelect = (languageCode) => {
    onLanguageChange(languageCode);
    setIsLanguageDropdownOpen(false);
  };

  const handleSlideSelect = (slideIndex) => {
    if (onSlideNavigate && isSlideAccessible(slideIndex)) {
      onSlideNavigate(slideIndex);
      setIsSlideDropdownOpen(false);
    }
  };

  // Check if a slide is accessible (completed or current)
  const isSlideAccessible = (slideIndex) => {
    const slideNumber = slideIndex + 1; // Convert to 1-based
    // Current slide is always accessible
    if (slideIndex === currentSlideIndex) return true;
    // Completed slides are accessible
    if (completedSlides.includes(slideNumber)) return true;
    // Next slide after highest completed is accessible
    if (completedSlides.length > 0) {
      const highestCompleted = Math.max(...completedSlides);
      return slideNumber <= highestCompleted + 1;
    }
    // If no completed slides, only slide 1 is accessible
    return slideNumber === 1;
  };

  // Get slide title from lesson data
  const getSlideTitle = (slideIndex) => {
    const slides = lessonData?.Slides || lessonData?.slides || [];
    const slide = slides[slideIndex];
    if (!slide) return `${t('ui.navigation.slide')} ${slideIndex + 1}`;
    
    // Try to get slide title from different possible locations
    const slideNumber = slide.SlideNumber || slide.slideNumber || slideIndex + 1;
    const titleKey = `ui.slideTitles.${slideNumber}`;
    const title = t(titleKey);
    
    // If translation not found, use generic title
    if (title === titleKey) {
      return `${t('ui.navigation.slide')} ${slideIndex + 1}`;
    }
    return title;
  };

  return (
    <div className={`top-bar ${!isVisible ? 'hidden' : ''}`}>
      <div className="top-bar-content">
        {/* Logo Section */}
        <div className="logo-section">
          <img 
            src={YDALogo}
            alt="YDA - Yas Driving Academy" 
            className="logo-image"
            onError={(e) => {
              // Show placeholder text if image not found
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'block';
            }}
          />
        </div>

        {/* Slide Navigation Dropdown */}
        <div className="slide-navigation" ref={slideDropdownRef}>
          <button
            className="slide-nav-btn"
            onClick={toggleSlideDropdown}
            title={t('ui.navigation.selectSlide')}
            aria-label={t('ui.navigation.selectSlide')}
          >
            <span className="slide-current">
              | {String(currentSlideIndex + 1).padStart(2, '0')}
            </span>
            <svg 
              width="12" 
              height="12" 
              viewBox="0 0 24 24" 
              fill="white"
              className={`dropdown-arrow ${isSlideDropdownOpen ? 'open' : ''}`}
            >
              <path d="M7 10l5 5 5-5z"/>
            </svg>
          </button>
          {isSlideDropdownOpen && isVisible && (
            <div className="slide-dropdown">
              <div className="slide-dropdown-list">
                {Array.from({ length: totalSlides }, (_, index) => {
                  const slideNumber = index + 1;
                  const isAccessible = isSlideAccessible(index);
                  const isCurrent = index === currentSlideIndex;
                  const isCompleted = completedSlides.includes(slideNumber);
                  
                  return (
                    <button
                      key={index}
                      className={`slide-option ${isCurrent ? 'current' : ''} ${isCompleted ? 'completed' : ''} ${!isAccessible ? 'disabled' : ''}`}
                      onClick={() => handleSlideSelect(index)}
                      disabled={!isAccessible}
                      title={isAccessible ? getSlideTitle(index) : t('ui.navigation.slideNotAccessible')}
                    >
                      <div className="slide-option-content">
                        <span className="slide-number">
                          {String(slideNumber).padStart(2, '0')}
                        </span>
                        <span className="slide-title">
                          {getSlideTitle(index)}
                        </span>
                        <div className="slide-status">
                          {isCurrent && (
                            <span className="status-indicator current-indicator">
                              {t('ui.navigation.current')}
                            </span>
                          )}
                          {isCompleted && !isCurrent && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="#4CAF50">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                          )}
                          {!isAccessible && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="#999">
                              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                            </svg>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Controls Section */}
        <div className="controls-section">
          {/* Language Selector */}
          <div className="language-controls" ref={dropdownRef}>
            <button
              className="language-icon-btn"
              onClick={toggleLanguageDropdown}
              title={t('ui.topBar.selectLanguage')}
              aria-label={t('ui.topBar.selectLanguage')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
            </button>
            {isLanguageDropdownOpen && (
              <div className="language-dropdown">
                {AVAILABLE_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    className={`language-option ${currentLanguage === lang.code ? 'active' : ''}`}
                    onClick={() => handleLanguageSelect(lang.code)}
                  >
                    <span className="language-name">{lang.nativeName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Audio Toggle */}
          <div className="audio-controls">
            <button
              className={`speaker-btn ${isAudioMuted ? 'muted' : ''}`}
              onClick={onAudioToggle}
              title={isAudioMuted ? t('ui.topBar.unmuteAudio') : t('ui.topBar.muteAudio')}
              aria-label={isAudioMuted ? t('ui.topBar.unmuteAudio') : t('ui.topBar.muteAudio')}
            >
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                className="speaker-icon"
              >
                {isAudioMuted ? (
                  // Muted speaker icon with X
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                ) : (
                  // Active speaker icon
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBar; 